import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, FolderHeart, FileText, Download, CheckCircle2, GraduationCap, PenLine, Save, Send, Briefcase, Flag, BrainCircuit } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { TYPE_SECTIONS, LivrableEditor } from '../adrns/livrableEditors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORG_TYPES = { porteur: 'Porteur de projet', entreprise: 'Entreprise', association: 'Association', dirigeant: 'Dirigeant' };
const STATUTS = { actif: 'bg-emerald-50 text-emerald-700 border-emerald-200', pause: 'bg-amber-50 text-amber-700 border-amber-200', termine: 'bg-stone-100 text-stone-600 border-stone-200' };

function LivrableView({ liv }) {
    const c = liv.content || {};
    if (TYPE_SECTIONS[liv.type]) {
        return (
            <div className="grid sm:grid-cols-2 gap-3">
                {TYPE_SECTIONS[liv.type].map(([k, label]) => (
                    <div key={k} className="border border-border rounded-lg p-3">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
                        <ul className="space-y-1 text-sm">
                            {(c.sections?.[k] || []).map((it, i) => <li key={i} className="pl-3 relative"><span className="absolute left-0 text-primary">•</span>{it}</li>)}
                            {!(c.sections?.[k] || []).length && <li className="text-foreground/40">—</li>}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }
    if (liv.type === 'scoring') {
        const criteres = c.criteres || [];
        const total = criteres.reduce((s, x) => s + (Number(x.note) || 0), 0);
        return (
            <div className="space-y-2">
                <div className="font-heading text-3xl text-primary">{total}<span className="text-base text-muted-foreground">/100</span></div>
                {criteres.map((x, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm border-b border-border/60 pb-1.5">
                        <span className="flex-1">{x.nom}</span>
                        <span className="font-medium">{x.note}/{x.max || 10}</span>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div className="space-y-2">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.text || '—'}</p>
        </div>
    );
}

function AnalyseFinale({ analyse }) {
    if (!analyse) return null;
    return (
        <div className="mt-3 rounded-lg border border-primary/25 bg-primary/5 p-3" data-testid="porteur-analyse-finale">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary font-semibold mb-1.5">
                <BrainCircuit size={13} /> Analyse Intelligence African Valley 360°
            </div>
            <div className="text-sm leading-relaxed space-y-1">
                {analyse.text.split('\n').map((l, i) => {
                    const t = l.trim();
                    if (!t) return <div key={i} className="h-1.5" />;
                    if (t.startsWith('#')) return <div key={i} className="font-medium text-primary mt-2">{t.replace(/^#+\s*/, '')}</div>;
                    if (t.startsWith('- ') || t.startsWith('• ')) return <div key={i} className="pl-4 relative"><span className="absolute left-0 text-primary">•</span>{t.slice(2)}</div>;
                    return <p key={i}>{t.replace(/\*\*/g, '')}</p>;
                })}
            </div>
        </div>
    );
}

function PorteurFormCard({ liv, hdrs, onChanged }) {
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState(liv.content);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);

    const patch = async (submit) => {
        setBusy(true);
        try {
            await axios.patch(`${API}/adrns/porteur/livrables/${liv.id}`, { content, submit }, hdrs());
            toast.success(submit ? 'Formulaire soumis à votre accompagnateur' : 'Brouillon enregistré');
            setDirty(false);
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    return (
        <div className="border border-border rounded-lg overflow-hidden" data-testid={`porteur-form-${liv.key}`}>
            <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-surface-muted/50 text-sm">
                <PenLine size={14} className="text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                    <span className="font-medium">{liv.title}</span>
                    <div className="text-xs text-muted-foreground truncate">{liv.hint}</div>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">Module {liv.module_id}</span>
                {liv.statut === 'soumis' && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200 shrink-0" data-testid={`porteur-form-soumis-${liv.key}`}>
                        En attente de révision
                    </span>
                )}
            </button>
            {open && (
                <div className="p-4 border-t border-border space-y-3">
                    <LivrableEditor type={liv.type} value={content} onChange={c => { setContent(c); setDirty(true); }}
                        hint={liv.hint} testPrefix={`porteur-ed-${liv.key}`} />
                    <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => patch(false)} disabled={busy || !dirty} data-testid={`porteur-form-save-${liv.key}`}>
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} className="mr-1.5" /> Enregistrer le brouillon</>}
                        </Button>
                        <Button size="sm" onClick={() => patch(true)} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid={`porteur-form-submit-${liv.key}`}>
                            <Send size={13} className="mr-1.5" /> Soumettre à mon accompagnateur
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function DossierView({ dossierId, session }) {
    const [data, setData] = useState(null);
    const [openLiv, setOpenLiv] = useState(null);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    useEffect(() => {
        axios.get(`${API}/adrns/porteur/dossiers/${dossierId}`, hdrs())
            .then(r => setData(r.data)).catch(() => setData({ error: true }));
        // eslint-disable-next-line
    }, [dossierId]);

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>;
    if (data.error) return <p className="text-sm text-foreground/60">Impossible de charger ce dossier.</p>;
    const { dossier: d, accompagnateurs, modules, livrables, formulaires = [], documents, missions = [] } = data;
    const reload = () => axios.get(`${API}/adrns/porteur/dossiers/${dossierId}`, hdrs())
        .then(r => setData(r.data)).catch(() => {});

    const downloadDoc = async (doc) => {
        try {
            const r = await axios.get(`${API}/adrns/documents/${doc.id}/download`, { ...hdrs(), responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = doc.name; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Téléchargement impossible'); }
    };

    return (
        <div className="space-y-6" data-testid="porteur-dossier-view">
            <div className="card-soft p-6">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="eyebrow mb-1">{ORG_TYPES[d.org_type] || d.org_type}</div>
                        <h2 className="font-heading text-2xl">{d.porteur_name}</h2>
                        {d.besoin && <p className="text-sm text-foreground/70 mt-2">{d.besoin}</p>}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUTS[d.statut] || STATUTS.actif}`}>{d.statut}</span>
                </div>
                {accompagnateurs.length > 0 && (
                    <div className="mt-4 text-sm text-foreground/70">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-2">Votre équipe d'accompagnement :</span>
                        {accompagnateurs.map(m => m.full_name || m.email).join(' · ')}
                    </div>
                )}
            </div>

            {missions.length > 0 && (
                <div className="card-soft p-6" data-testid="porteur-missions">
                    <h3 className="font-heading text-xl mb-4 flex items-center gap-2"><Briefcase size={18} className="text-primary" /> Missions d'accompagnement</h3>
                    <div className="space-y-4">
                        {missions.map(m => (
                            <div key={m.id} className="border border-border rounded-lg p-4" data-testid={`porteur-mission-${m.id}`}>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="font-medium">{m.titre}</div>
                                        <div className="text-xs text-muted-foreground">{m.type_nom} · {m.livrables_valides}/{m.livrables_total} livrables validés</div>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${m.statut === 'cloturee' ? 'bg-stone-100 text-stone-600 border-stone-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                                        {m.statut === 'cloturee' ? 'Clôturée' : 'En cours'}
                                    </span>
                                </div>
                                <Progress value={m.livrables_total ? (m.livrables_valides / m.livrables_total) * 100 : 0} className="h-1.5 mt-3" />
                                {(m.jalons || []).length > 0 && (
                                    <div className="mt-3 space-y-1">
                                        {m.jalons.map(j => (
                                            <div key={j.id} className="flex items-center gap-2 text-xs">
                                                {j.done ? <CheckCircle2 size={12} className="text-emerald-600" /> : <Flag size={12} className="text-muted-foreground" />}
                                                <span className={j.done ? 'line-through text-muted-foreground' : ''}>{j.titre}</span>
                                                <span className="text-muted-foreground ml-auto">{j.echeance}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {m.prochaine_reunion && (
                                    <div className="mt-2 text-xs text-foreground/70">
                                        📅 Prochaine réunion : <b>{m.prochaine_reunion.titre}</b> — {new Date(m.prochaine_reunion.date).toLocaleString('fr-FR')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="card-soft p-6">
                <h3 className="font-heading text-xl mb-4 flex items-center gap-2"><GraduationCap size={18} className="text-primary" /> Votre parcours d'accompagnement</h3>
                {modules.length === 0 ? (
                    <p className="text-sm text-foreground/60">Aucun module activé pour le moment.</p>
                ) : (
                    <div className="space-y-4">
                        {modules.map(m => (
                            <div key={m.id} data-testid={`porteur-module-${m.id}`}>
                                <div className="flex items-center justify-between text-sm mb-1.5">
                                    <div><span className="text-xs uppercase tracking-wider text-muted-foreground mr-2">Module {m.id}</span><span className="font-medium">{m.nom}</span></div>
                                    <span className="text-xs text-muted-foreground">{m.valides}/{m.total} livrables validés</span>
                                </div>
                                <Progress value={(m.valides / Math.max(m.total, 1)) * 100} className="h-1.5" />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {formulaires.length > 0 && (
                <div className="card-soft p-6 border-primary/20" data-testid="porteur-formulaires">
                    <h3 className="font-heading text-xl mb-1 flex items-center gap-2"><PenLine size={18} className="text-primary" /> Formulaires à compléter ({formulaires.length})</h3>
                    <p className="text-sm text-foreground/60 mb-4">Votre accompagnateur vous a confié ces formulaires. Enregistrez vos brouillons, puis soumettez quand vous êtes prêt.</p>
                    <div className="space-y-2">
                        {formulaires.map(liv => <PorteurFormCard key={liv.id} liv={liv} hdrs={hdrs} onChanged={reload} />)}
                    </div>
                </div>
            )}

            <div className="card-soft p-6">
                <h3 className="font-heading text-xl mb-4 flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /> Livrables validés ({livrables.length})</h3>
                {livrables.length === 0 ? (
                    <p className="text-sm text-foreground/60">Les livrables validés par votre accompagnateur apparaîtront ici.</p>
                ) : (
                    <div className="space-y-2">
                        {livrables.map(liv => (
                            <div key={liv.id} className="border border-border rounded-lg overflow-hidden" data-testid={`porteur-livrable-${liv.key}`}>
                                <button onClick={() => setOpenLiv(o => o === liv.id ? null : liv.id)}
                                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-surface-muted/50 text-sm">
                                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                                    <span className="font-medium flex-1">{liv.title}</span>
                                    <span className="text-xs text-muted-foreground">Module {liv.module_id}</span>
                                </button>
                                {openLiv === liv.id && (
                                    <div className="p-4 border-t border-border">
                                        <LivrableView liv={liv} />
                                        <AnalyseFinale analyse={liv.analyse_ia} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="card-soft p-6">
                <h3 className="font-heading text-xl mb-4">Documents partagés ({documents.length})</h3>
                {documents.length === 0 ? (
                    <p className="text-sm text-foreground/60">Aucun document pour le moment.</p>
                ) : (
                    <ul className="space-y-2">
                        {documents.map(doc => (
                            <li key={doc.id} className="flex items-center gap-3 border border-border rounded-lg px-3 py-2 text-sm">
                                <FileText size={15} className="text-primary shrink-0" />
                                <span className="flex-1 truncate">{doc.name}</span>
                                <button onClick={() => downloadDoc(doc)} className="text-muted-foreground hover:text-primary" data-testid={`porteur-doc-download-${doc.id}`}>
                                    <Download size={15} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function MonAccompagnement() {
    const { session } = useAuth();
    const [items, setItems] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!session) return;
        axios.get(`${API}/adrns/porteur/dossiers`, { headers: { Authorization: `Bearer ${session.access_token}` } })
            .then(r => { setItems(r.data.items); if (r.data.items[0]) setSelected(r.data.items[0].id); })
            .catch(() => setItems('error'));
    }, [session]);

    if (!items) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;
    if (items === 'error') {
        return (
            <div className="card-soft p-12 text-center text-foreground/60" data-testid="porteur-error">
                Impossible de charger vos dossiers pour le moment.
                <button onClick={() => window.location.reload()} className="block mx-auto mt-3 text-sm text-primary underline">Réessayer</button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="mon-accompagnement-page">
            <div>
                <div className="eyebrow mb-2">ADRNS · Espace porteur</div>
                <h1 className="font-heading text-4xl">Mon accompagnement</h1>
                <p className="text-sm text-foreground/60 mt-2">Suivez en lecture l'avancement de votre accompagnement par l'équipe African Valley / ADRNS.</p>
            </div>
            {items.length === 0 ? (
                <div className="card-soft p-12 text-center text-foreground/60" data-testid="porteur-empty">
                    <FolderHeart size={30} className="mx-auto mb-3 text-muted-foreground" />
                    Aucun dossier d'accompagnement ne vous est associé pour le moment.
                </div>
            ) : (
                <>
                    {items.length > 1 && (
                        <div className="flex gap-2 flex-wrap">
                            {items.map(d => (
                                <button key={d.id} onClick={() => setSelected(d.id)}
                                    className={`px-3 py-1.5 rounded-full border text-sm ${selected === d.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
                                    {d.porteur_name}
                                </button>
                            ))}
                        </div>
                    )}
                    {selected && <DossierView dossierId={selected} session={session} />}
                </>
            )}
        </div>
    );
}
